import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import { PageHeader } from "@/components/common/PageHeader";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { KpiCard } from "@/components/common/KpiCard";
import { fetchGroup, fetchGroupStudents } from "@/api/groups";
import { fetchGroupLessons } from "@/api/journal";
import { fetchStudents, createStudentEnrollment } from "@/api/students";
import { fetchPublicSettings } from "@/api/settings";
import { useAuth } from "@/auth/useAuth";
import type { Group } from "@/types/group";
import { DAYSET_LABELS, GROUP_STATUS_LABELS, STUDENT_STATUS_LABELS, daysToDayset } from "@/types/enums";
import { extractErrorMessage } from "@/types/common";
import { formatDate, formatMoney, formatPercent } from "@/utils/format";
import { GroupFormDialog } from "./GroupFormDialog";

function AddStudentToGroupDialog({ open, onClose, groupId }: { open: boolean; onClose: () => void; groupId: string }) {
  const queryClient = useQueryClient();
  const [studentId, setStudentId] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: students } = useQuery({
    queryKey: ["students", "for-enroll"],
    queryFn: () => fetchStudents({ status: "ACTIVE", limit: 500 }),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => createStudentEnrollment(studentId, { studentId, groupId, startDate }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["group-students", groupId] });
      void queryClient.invalidateQueries({ queryKey: ["group", groupId] });
      setStudentId("");
      onClose();
    },
    onError: (err) => setServerError(extractErrorMessage(err)),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Добавить ученика в группу</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Stack spacing={2} mt={1}>
          <TextField select label="Ученик" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
            {(students?.items ?? []).map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.fullName} · {s.phone}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Дата начала"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!studentId || mutation.isPending} onClick={() => mutation.mutate()}>
          Добавить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GroupRosterTab({ groupId, canEdit }: { groupId: string; canEdit: boolean }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canSeeFinance = hasPermission("fin.debts");
  const [addOpen, setAddOpen] = useState(false);
  const { data: students, isLoading, error } = useQuery({
    queryKey: ["group-students", groupId],
    queryFn: () => fetchGroupStudents(groupId),
  });

  return (
    <QueryState isLoading={isLoading} error={error}>
      <Stack direction="row" justifyContent="flex-end" mb={1.5}>
        {canEdit && (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Добавить ученика
          </Button>
        )}
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ФИО</TableCell>
            <TableCell>Посещаемость</TableCell>
            <TableCell>Средний балл</TableCell>
            {canSeeFinance && <TableCell>Долг</TableCell>}
            <TableCell>Статус</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(students ?? []).map((student) => (
            <TableRow key={student.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/students/${student.id}`)}>
              <TableCell>{student.fullName}</TableCell>
              <TableCell>{formatPercent(student.attendancePercent)}</TableCell>
              <TableCell>{student.averageGrade ?? "—"}</TableCell>
              {canSeeFinance && <TableCell>{formatMoney(student.debt ?? 0)}</TableCell>}
              <TableCell>
                <StatusChip label={STUDENT_STATUS_LABELS[student.status]} />
              </TableCell>
            </TableRow>
          ))}
          {(students ?? []).length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                В группе пока нет учеников.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <AddStudentToGroupDialog open={addOpen} onClose={() => setAddOpen(false)} groupId={groupId} />
    </QueryState>
  );
}

function GroupLessonsTab({ groupId }: { groupId: string }) {
  const { data: lessons, isLoading, error } = useQuery({
    queryKey: ["group-lessons", groupId],
    queryFn: () => fetchGroupLessons(groupId),
  });

  return (
    <QueryState isLoading={isLoading} error={error}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Дата</TableCell>
            <TableCell>Тема</TableCell>
            <TableCell>Домашнее задание</TableCell>
            <TableCell>Статус</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(lessons ?? []).map((lesson) => (
            <TableRow key={lesson.id}>
              <TableCell>{formatDate(lesson.date)}</TableCell>
              <TableCell>{lesson.topic || "—"}</TableCell>
              <TableCell>{lesson.homework || "—"}</TableCell>
              <TableCell>
                <StatusChip label={lesson.canceled ? "Урок отменён" : "Проведён"} />
              </TableCell>
            </TableRow>
          ))}
          {(lessons ?? []).length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                Занятий по этой группе пока не проводилось.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </QueryState>
  );
}

function GroupScheduleTab({ group }: { group: Group }) {
  const dayset = daysToDayset(group.days);
  return (
    <Box>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell component="th">Дни недели</TableCell>
            <TableCell>{dayset ? DAYSET_LABELS[dayset] : group.days.join(", ")}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Время</TableCell>
            <TableCell>{group.timeSlot}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Кабинет</TableCell>
            <TableCell>{group.roomName ?? group.roomId}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Преподаватель</TableCell>
            <TableCell>{group.teacherName ?? "Не назначен"}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th">Дата старта</TableCell>
            <TableCell>{formatDate(group.startDate)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

export function GroupDetailPage() {
  const { id = "" } = useParams();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("groups.edit");
  const canSeeFinance = hasPermission("fin.report");
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  const navigate = useNavigate();
  const { data: group, isLoading, error } = useQuery({
    queryKey: ["group", id],
    queryFn: () => fetchGroup(id),
    enabled: Boolean(id),
  });
  const { data: publicSettings } = useQuery({ queryKey: ["public-settings"], queryFn: fetchPublicSettings });
  const canMark = hasPermission("attendance.mark");

  return (
    <>
      <PageHeader
        title={group ? `Группа ${group.code}` : "Группа"}
        subtitle={
          group
            ? `${group.level} · ${(daysToDayset(group.days) && DAYSET_LABELS[daysToDayset(group.days)!]) ?? ""} ${group.timeSlot}`
            : undefined
        }
        actions={
          group && (
            <Box display="flex" gap={1} alignItems="center">
              <StatusChip label={GROUP_STATUS_LABELS[group.status]} />
              {canMark && (
                <Button size="small" variant="contained" onClick={() => navigate(`/journal?groupId=${group.id}`)}>
                  Отметить урок
                </Button>
              )}
              {canEdit && (
                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                  Изменить
                </Button>
              )}
            </Box>
          )
        }
      />
      <QueryState isLoading={isLoading} error={error}>
        {group && (
          <>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard label="Учеников" value={String(group.studentsCount ?? 0)} hint={group.seatsLeft != null ? `вместимость ${(group.studentsCount ?? 0) + group.seatsLeft}` : undefined} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard label="Посещаемость" value={formatPercent(group.attendancePercent ?? 0)} hint={`${group.lessonsCount ?? 0} уроков`} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Средний балл"
                  value={group.averageGrade ? group.averageGrade.toFixed(1) : "—"}
                  hint={`шкала 1–${publicSettings?.gradeScale ?? 5}`}
                />
              </Grid>
              {canSeeFinance && <Grid item xs={12} sm={6} md={3}>
                <KpiCard label="Доход группы за месяц" value={formatMoney(group.monthRevenue ?? 0)} />
              </Grid>}
            </Grid>

            <Paper className="crm-detail-panel" variant="outlined">
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons={false}>
                <Tab label="Состав" />
                <Tab label="Проведённые уроки" />
                <Tab label="Расписание" />
              </Tabs>
              <Box className="crm-detail-panel__body" p={3}>
                {tab === 0 && <GroupRosterTab groupId={group.id} canEdit={canEdit} />}
                {tab === 1 && <GroupLessonsTab groupId={group.id} />}
                {tab === 2 && <GroupScheduleTab group={group} />}
              </Box>
            </Paper>
          </>
        )}
      </QueryState>
      {group && <GroupFormDialog open={editOpen} onClose={() => setEditOpen(false)} group={group} />}
    </>
  );
}
